import { useState } from 'react';
import { useCuttingStore } from '../state/useCuttingStore';
import type { EdgeAssignment, EdgeType } from '../model/types';

/**
 * Block 14 «Раскрой» — графический селектор кромок детали (PR #10a-EDGE, CUT-3.3).
 *
 * Пиксельный порт `project.html:727-758` (#edge-modal) + логика `app.js:276-340`
 * (openEdgeDialog / cycleEdgeSide / saveEdgeDialog / sideLabelEdge). Сетка
 * `.edge-modal-grid`: 4 стороны (top/left/right/bottom) вокруг центра «деталь»,
 * тап по стороне циклически Нет → Кромка 1 → Кромка 2 → Нет.
 *
 * Шелл — прямая `.sc-modal2-overlay`-разметка (как `SheetSizeDialog`, прецедент
 * того же feature для графических модалок): сохраняет × в шапке (вербатим
 * прототипа), чего `CuttingModal2` (title=string) не умеет. ОК → `updatePart(id,
 * { edges })` → стор dirty → `EdgeMiniSvg` в ячейке перерисовывается из стора.
 *
 * Цикл оперирует только `type1`/`type2`/`''` (как прототип); модель допускает
 * type3/type4 (контракт), но этот селектор их не производит — неизвестное
 * входное значение трактуется как «Нет» (стартовая точка цикла).
 *
 * @see Помощник — Прототип Раскроя/01-prototype-vanilla/project.html:727-758
 */

/** Сторона детали (порядок прототипа app.js:279: top/right/bottom/left). */
type Side = 'top' | 'right' | 'bottom' | 'left';

/** Цикл значения стороны (app.js:294-301): '' → type1 → type2 → ''. */
const NEXT: Record<string, EdgeType> = { '': 'type1', type1: 'type2', type2: '' };

/** Подпись стороны (app.js:302-304). Неизвестное → «Нет». */
function sideLabel(t: EdgeType): string {
  if (t === 'type1') return 'Тип 1';
  if (t === 'type2') return 'Тип 2';
  return 'Нет';
}

/** Человеко-читаемая сторона (для aria-label). */
const SIDE_RU: Record<Side, string> = {
  top: 'сверху',
  right: 'справа',
  bottom: 'снизу',
  left: 'слева',
};

const EMPTY_EDGES: EdgeAssignment = { top: '', right: '', bottom: '', left: '' };

/** Нормализация модельного значения к циклу селектора (type1/type2/''). */
function normalize(t: EdgeType): EdgeType {
  return t === 'type1' || t === 'type2' ? t : '';
}

export function EdgeModal({
  partId,
  partLabel,
  onClose,
}: {
  partId: string;
  partLabel: number;
  onClose: () => void;
}) {
  const updatePart = useCuttingStore((s) => s.updatePart);
  // Начальное состояние снимаем из стора один раз (модалка — транзиентный шаг).
  const [edges, setEdges] = useState<EdgeAssignment>(() => {
    const part = useCuttingStore.getState().document.parts.find((p) => p.id === partId);
    const e = part?.edges ?? EMPTY_EDGES;
    return {
      top: normalize(e.top),
      right: normalize(e.right),
      bottom: normalize(e.bottom),
      left: normalize(e.left),
    };
  });

  const cycle = (side: Side) => {
    setEdges((prev) => ({ ...prev, [side]: NEXT[prev[side]] ?? 'type1' }));
  };

  const apply = () => {
    updatePart(partId, { edges });
    onClose();
  };

  const sideBtn = (side: Side) => {
    const t = edges[side];
    const typeClass = t === 'type1' ? ' type1' : t === 'type2' ? ' type2' : '';
    return (
      <button
        type="button"
        className={`edge ${side}${typeClass}`}
        aria-label={`Кромка ${SIDE_RU[side]}: ${sideLabel(t)}`}
        onClick={() => cycle(side)}
      >
        {sideLabel(t)}
      </button>
    );
  };

  return (
    <div
      className="sc-modal2-overlay visible"
      role="dialog"
      aria-modal="true"
      aria-label={`Кромки детали ${partLabel}`}
    >
      <div className="sc-modal2">
        <div className="sc-modal2-title">
          <span>Кромки детали</span>
          <button type="button" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>
        <div className="sc-modal2-content">
          <p className="edge-modal-hint">Укажите стороны с кромкой</p>
          <div className="edge-modal-grid">
            {sideBtn('top')}
            {sideBtn('left')}
            <div className="edge-modal-center">деталь</div>
            {sideBtn('right')}
            {sideBtn('bottom')}
          </div>
          <div className="edge-modal-legend">
            Тап по стороне циклически: Нет → Кромка 1 → Кромка 2 → Нет
          </div>
        </div>
        <div className="sc-modal2-footer">
          <button type="button" className="sc-modal-btn" onClick={onClose}>
            Отмена
          </button>
          <button type="button" className="sc-modal-btn primary" onClick={apply}>
            Применить
          </button>
        </div>
      </div>
    </div>
  );
}
